/* Generic clearance dialog used by:
     - Repayments page    → POST /repayments/{id}/mark-deposited
     - Application page   → POST /repayments/{id}/mark-deposited
     - Application page   → POST /finance/applications/{appId}/cheques/{chequeId}/mark-cleared

   Captures (1) date the cheque was banked, (2) which bank it was deposited
   into (dropdown of active banks from QH_DATA.banks), and (3) a free-text
   comment. The caller passes an `onSubmit({ clearedOn, depositBankId,
   comment })` callback so this component stays HTTP-agnostic — the same
   dialog drives both repayment and guarantor clearance. */
const { useState: useStateCC, useEffect: useEffectCC, useRef: useRefCC } = React;

function ClearChequeModal({
  title = 'Mark cheque cleared',
  subtitle = '',
  // Pre-populate inputs from an already-cleared row (allows the modal to
  // double as an "edit clearance" UI later without code changes). All optional.
  defaultDate = '',
  defaultBankId = '',
  defaultComment = '',
  // Optional summary block at the top of the modal: { issuer, cheque, bank, dueDate, amount }
  cheque = null,
  onClose,
  onSubmit,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useStateCC(defaultDate || today);
  const [bankId, setBankId] = useStateCC(defaultBankId || '');
  const [comment, setComment] = useStateCC(defaultComment || '');
  const [busy, setBusy] = useStateCC(false);
  const [err, setErr] = useStateCC('');
  const firstInput = useRefCC(null);

  // Active banks only — inactive ones shouldn't accept new deposits.
  const banks = (window.QH_DATA?.banks || []).filter(b => b.active !== false);

  useEffectCC(() => { firstInput.current?.focus(); }, []);

  const submit = async () => {
    setErr('');
    if (!date) { setErr('Cleared-on date is required.'); return; }
    setBusy(true);
    try {
      await onSubmit({
        clearedOn: date,
        depositBankId: bankId || null,
        comment: (comment || '').trim() || null,
      });
      onClose?.();
    } catch (e) {
      setErr(e?.message || 'Failed to record clearance.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: 480}}>
        <div className="modal-header">
          <div>
            <h3>{title}</h3>
            {subtitle && <div className="text-xs muted" style={{marginTop: 2}}>{subtitle}</div>}
          </div>
          <button className="icon-btn close" onClick={onClose} disabled={busy} style={{marginLeft: 'auto'}}>
            <I.X/>
          </button>
        </div>

        <div className="modal-body" style={{display: 'flex', flexDirection: 'column', gap: 12}}>
          {cheque && (
            <div style={{padding: 10, background: 'var(--bg-sunken)', borderRadius: 4, fontSize: 12, lineHeight: 1.6}}>
              {cheque.issuer && <div><span className="muted">Issuer:</span> {cheque.issuer}</div>}
              {cheque.cheque && <div><span className="muted">Cheque #:</span> <span className="font-mono">{cheque.cheque}</span></div>}
              {cheque.bank && <div><span className="muted">Drawer bank:</span> {cheque.bank}</div>}
              {cheque.dueDate && <div><span className="muted">Due:</span> <span className="font-mono">{cheque.dueDate}</span></div>}
              {cheque.amount != null && <div><span className="muted">Amount:</span> <b className="font-mono">{Number(cheque.amount).toFixed(3)} BHD</b></div>}
            </div>
          )}

          <div className="field">
            <label>Cleared on</label>
            <input ref={firstInput} className="input font-mono" type="date"
                   value={date} onChange={e => setDate(e.target.value)} disabled={busy}/>
          </div>

          <div className="field">
            <label>Deposited into</label>
            <select className="select" value={bankId} onChange={e => setBankId(e.target.value)} disabled={busy}>
              <option value="">— Select bank —</option>
              {banks.map(b => (
                <option key={b.id} value={b.id}>
                  {b.name}{b.code ? ` · ${b.code}` : ''}{b.accountNumber ? ` · ${b.accountNumber}` : ''}
                </option>
              ))}
            </select>
            {banks.length === 0 && (
              <div className="text-xs muted" style={{marginTop: 4}}>
                No active banks configured. Add one under <b>Banks</b> first.
              </div>
            )}
          </div>

          <div className="field">
            <label>Comment <span className="text-xs muted">(optional)</span></label>
            <textarea className="textarea" rows={3} value={comment} maxLength={512}
                      onChange={e => setComment(e.target.value)} disabled={busy}
                      placeholder="Notes — e.g. deposited at branch, cleared with delay, etc."/>
          </div>

          {err && <div style={{color: 'var(--neg)', fontSize: 12}}>{err}</div>}
        </div>

        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <div style={{flex: 1}}/>
          <button className="btn primary" onClick={submit} disabled={busy || !date}>
            <I.Check/> {busy ? 'Saving…' : 'Mark cleared'}
          </button>
        </div>
      </div>
    </div>
  );
}

window.ClearChequeModal = ClearChequeModal;
